import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { MotionModal } from '../../../components/motion-modal/motion-modal';
import {
  useCancelSeriesRecording,
  useRecordSeries,
  useRescheduleSeries,
} from '../../../queries/ats/recordings/mutations';
import { LinkedEpisodeRecording, SeriesRecording } from '../../../queries/ats/recordings/types/recording';
import { EpisodeAsset } from '../../../queries/ats/titles/types/asset';
import { ProgramSource } from '../../../queries/ats/titles/types/sources';
import { useRecordQueue } from './record-queue';
import { getDeleteMode } from './utils';

export interface RecordSeriesButtonsProps {
  asset: EpisodeAsset;
  source: ProgramSource;
}

export function RecordSeriesButtons({ asset, source }: RecordSeriesButtonsProps) {
  return (
    <Fragment>
      <RecordRerunsButton asset={asset} source={source} />
      <RecordChronologicalButton asset={asset} source={source} />
    </Fragment>
  );
}

interface RescheduleSeriesButtonsProps {
  asset: EpisodeAsset;
  recording: SeriesRecording | LinkedEpisodeRecording;
}

export function RescheduleSeriesButtons({ asset, recording }: RescheduleSeriesButtonsProps) {
  return (
    <Fragment>
      <RescheduleRerunsButton asset={asset} recording={recording} />
      <RescheduleChronologicalButton asset={asset} recording={recording} />
    </Fragment>
  );
}

function RescheduleRerunsButton({ asset, recording }: RescheduleSeriesButtonsProps) {
  const queue = useRecordQueue();
  const { t } = useTranslation('recordings');

  const { mutateAsync: rescheduleSeries, isLoading } = useRescheduleSeries(asset);

  const deleteMode = recording.deleteMode;

  return (
    <MotionModal.Button
      onClick={async () => {
        queue.push('record-series');
        await rescheduleSeries({ recording, deleteMode, rerunsAllowed: true });
        queue.pop('record-series');
      }}
      isLoading={isLoading}
      isDisabled={queue.size > 0}
    >
      {t('record-series')}
    </MotionModal.Button>
  );
}

function RescheduleChronologicalButton({ asset, recording }: RescheduleSeriesButtonsProps) {
  const queue = useRecordQueue();
  const { t } = useTranslation('recordings');

  const { mutateAsync: rescheduleSeries, isLoading } = useRescheduleSeries(asset);

  const deleteMode = recording.deleteMode;

  return (
    <MotionModal.Button
      onClick={async () => {
        queue.push('record-series');
        await rescheduleSeries({ recording, deleteMode, rerunsAllowed: false });
        queue.pop('record-series');
      }}
      isLoading={isLoading}
      isDisabled={queue.size > 0}
    >
      {t('record-series-chronological')}
    </MotionModal.Button>
  );
}

function RecordRerunsButton({ asset, source }: RecordSeriesButtonsProps) {
  const queue = useRecordQueue();
  const { t } = useTranslation('recordings');

  const { mutateAsync: recordSeries, isLoading } = useRecordSeries(asset);

  const deleteMode = getDeleteMode(source.recordings);

  return (
    <MotionModal.Button
      onClick={async () => {
        queue.push('record-series');
        await recordSeries({ source, deleteMode, rerunsAllowed: true });
        queue.pop('record-series');
      }}
      isLoading={isLoading}
      isDisabled={queue.size > 0}
    >
      {t('record-series')}
    </MotionModal.Button>
  );
}

function RecordChronologicalButton({ asset, source }: RecordSeriesButtonsProps) {
  const queue = useRecordQueue();
  const { t } = useTranslation('recordings');

  const { mutateAsync: recordSeries, isLoading } = useRecordSeries(asset);

  const deleteMode = getDeleteMode(source.recordings);

  return (
    <MotionModal.Button
      onClick={async () => {
        queue.push('record-chronological');
        await recordSeries({ source, deleteMode, rerunsAllowed: false });
        queue.pop('record-chronological');
      }}
      isLoading={isLoading}
      isDisabled={queue.size > 0}
    >
      {t('record-series-chronological')}
    </MotionModal.Button>
  );
}

export interface CancelSeriesRecordingButtonProps {
  recording: SeriesRecording | LinkedEpisodeRecording;
}

export function CancelSeriesRecordingButton({ recording }: CancelSeriesRecordingButtonProps) {
  const queue = useRecordQueue();
  const { t } = useTranslation('recordings');

  const { mutateAsync: cancelSeries, isLoading } = useCancelSeriesRecording({}, recording);

  return (
    <MotionModal.Button
      onClick={async () => {
        queue.push('cancel-record-series');
        await cancelSeries(recording.periodicTaskId);
        queue.pop('cancel-record-series');
      }}
      isLoading={isLoading}
      isDisabled={isLoading}
    >
      {t('cancel-series-recording')}
    </MotionModal.Button>
  );
}
